import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Crown, Users, Check, Sparkles, ArrowLeft, Star, Shield, FileText, Heart, 
  Car, Bell, Lock, Home, PawPrint, UserPlus, QrCode, Calendar, Loader2,
  RefreshCw, AlertCircle, WifiOff, ExternalLink, CheckCircle2, XCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useEntitlement, SUBSCRIPTION_TIERS } from '@/contexts/EntitlementContext';
import { useDemo } from '@/contexts/DemoContext';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  initializePurchases,
  purchasePlan,
  restorePurchases,
  openSubscriptionManagement,
  getPurchaseState,
  resetPurchaseState,
  getLastError,
  isNativePurchaseSupported,
  isIOS,
  isAndroid,
  isWeb,
  getProductPrice,
  PurchaseState,
} from '@/services/nativePurchaseService';

/**
 * SubscriptionPlansPage - Full subscription management with native IAP support
 * 
 * Handles:
 * - New purchases (Premium, Family)
 * - Upgrades (Premium → Family)
 * - Restore purchases
 * - Manage subscription
 * - All purchase states and edge cases
 * 
 * Platform-specific:
 * - iOS: Uses StoreKit 2 via native plugin
 * - Android: Uses Google Play Billing via native plugin
 * - Web: Uses Stripe checkout
 */

// Feature lists
const PREMIUM_FEATURES = [
  { icon: FileText, text: 'Unlimited medical records' },
  { icon: FileText, text: 'Document & insurance storage' },
  { icon: Heart, text: 'Doctor Summary & PDF exports' },
  { icon: Heart, text: 'Medical history tools' },
  { icon: Car, text: 'Vehicle records & VIN lookup' },
  { icon: Bell, text: 'Advanced reminders' },
  { icon: Lock, text: 'App lock security' },
  { icon: Home, text: 'Household information' },
  { icon: PawPrint, text: 'Pet information tracking' },
];

const FAMILY_FEATURES = [
  { icon: Check, text: 'Everything in Premium', highlight: true },
  { icon: Users, text: 'Up to 10 family profiles' },
  { icon: Users, text: 'Network' },
  { icon: UserPlus, text: 'Caregiver access' },
  { icon: QrCode, text: 'Emergency sharing with QR codes' },
  { icon: Home, text: 'Shared household info' },
  { icon: Calendar, text: 'Family calendar & events' },
];

const FREE_FEATURES = [
  { icon: Shield, text: '1 emergency profile' },
  { icon: Heart, text: 'Basic medical info' },
  { icon: Users, text: '3 emergency contacts' },
  { icon: Bell, text: '5 reminders' },
  { icon: FileText, text: 'Limited storage' },
];

// Purchase state messages
const STATE_MESSAGES = {
  [PurchaseState.LOADING_PRODUCTS]: 'Loading subscription options...',
  [PurchaseState.PURCHASING]: 'Processing your purchase...',
  [PurchaseState.VERIFYING]: 'Verifying purchase...',
  [PurchaseState.RESTORING]: 'Restoring your purchases...',
  [PurchaseState.OFFLINE]: 'You appear to be offline',
  [PurchaseState.STORE_UNAVAILABLE]: 'Store temporarily unavailable',
};

function SubscriptionPlansPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentTier, refreshEntitlements, isDemoMode, isExpired } = useEntitlement();
  const { exitDemoMode } = useDemo();
  const { user, isAuthenticated } = useAuth();
  
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [purchaseState, setPurchaseState] = useState(PurchaseState.IDLE);
  const [storeInitialized, setStoreInitialized] = useState(false);
  const [storeError, setStoreError] = useState(null);
  const [isPurchasing, setIsPurchasing] = useState(false); // Prevents duplicate taps
  
  // Get state from navigation
  const requiredTier = location.state?.requiredTier;
  const returnPath = location.state?.returnPath || '/';

  // Initialize native purchases on mount
  useEffect(() => {
    const init = async () => {
      if (isNativePurchaseSupported()) {
        setPurchaseState(PurchaseState.LOADING_PRODUCTS);
        const result = await initializePurchases();
        setStoreInitialized(result.success);
        if (!result.success) {
          setStoreError(result.error);
          if (result.offline) {
            setPurchaseState(PurchaseState.OFFLINE);
          } else {
            setPurchaseState(PurchaseState.STORE_UNAVAILABLE);
          }
        } else {
          setPurchaseState(PurchaseState.PRODUCTS_LOADED);
        }
      } else {
        // Web platform - always available via Stripe
        setStoreInitialized(true);
        setPurchaseState(PurchaseState.IDLE);
      }
    };
    init();
    
    return () => {
      resetPurchaseState();
    };
  }, []);

  // Get localized price from store (if available)
  const getPriceDisplay = useCallback((planId, fallback) => {
    const storePrice = getProductPrice(planId);
    return storePrice || fallback;
  }, [storeInitialized]);

  // Handle plan selection
  const handleSelectPlan = (planId) => {
    if (isPurchasing) return;
    setSelectedPlan(planId);
  };

  // Handle purchase
  const handlePurchase = async () => {
    if (!selectedPlan || isPurchasing) return;

    // Demo mode check
    if (isDemoMode) {
      toast.error('Please create an account to subscribe', {
        description: 'Exiting demo mode...',
      });
      exitDemoMode();
      navigate('/register');
      return;
    }

    // Not authenticated
    if (!isAuthenticated || !user) {
      toast.error('Please sign in to subscribe');
      navigate('/login', { state: { returnPath: '/subscription/plans' } });
      return;
    }

    setIsPurchasing(true);
    setPurchaseState(PurchaseState.PURCHASING);

    try {
      const result = await purchasePlan(selectedPlan, user.user_id, isDemoMode);

      if (result.success) {
        if (result.redirecting) {
          // Stripe redirect - page will navigate away
          return;
        }
        
        setPurchaseState(PurchaseState.SUCCESS);
        toast.success('Subscription activated!', {
          description: `Welcome to ${result.tier === 'family' ? 'Family' : 'Premium'}!`,
        });
        
        // Refresh entitlements
        await refreshEntitlements();
        
        // Navigate back after success
        setTimeout(() => {
          navigate(returnPath);
        }, 1500);
        
      } else if (result.cancelled) {
        setPurchaseState(PurchaseState.CANCELLED);
        toast.info('Purchase cancelled');
        
      } else if (result.offline) {
        setPurchaseState(PurchaseState.OFFLINE);
        toast.error('Network unavailable', {
          description: 'Please check your connection and try again.',
        });
        
      } else {
        setPurchaseState(PurchaseState.FAILED);
        toast.error('Purchase failed', {
          description: result.error || 'Please try again.',
        });
      }
    } catch (error) {
      console.error('Purchase error:', error);
      setPurchaseState(PurchaseState.FAILED);
      toast.error('Something went wrong', {
        description: 'Please try again later.',
      });
    } finally {
      setIsPurchasing(false);
    }
  };

  // Handle restore purchases
  const handleRestore = async () => {
    if (isPurchasing) return;

    if (!isAuthenticated || !user) {
      toast.error('Please sign in to restore purchases');
      return;
    }

    setIsPurchasing(true);
    setPurchaseState(PurchaseState.RESTORING);

    try {
      const result = await restorePurchases(user.user_id);

      if (result.success) {
        if (result.empty) {
          setPurchaseState(PurchaseState.RESTORE_EMPTY);
          toast.info('No purchases found', {
            description: result.message || 'No previous subscriptions to restore.',
          });
        } else {
          setPurchaseState(PurchaseState.RESTORE_SUCCESS);
          toast.success('Purchases restored!', {
            description: `Your ${result.tier} subscription is active.`,
          });
          await refreshEntitlements();
        }
      } else {
        setPurchaseState(PurchaseState.FAILED);
        toast.error('Restore failed', {
          description: result.error || 'Please try again.',
        });
      }
    } catch (error) {
      console.error('Restore error:', error);
      setPurchaseState(PurchaseState.FAILED);
      toast.error('Something went wrong');
    } finally {
      setIsPurchasing(false);
    }
  };

  // Handle manage subscription
  const handleManageSubscription = async () => {
    const result = await openSubscriptionManagement();
    if (result.redirect) {
      navigate(result.redirect);
    }
  };

  // Back navigation
  const handleBack = () => {
    if (returnPath) {
      navigate(returnPath);
    } else {
      navigate(-1);
    }
  };

  // Check if user can upgrade (Premium → Family)
  const canUpgrade = currentTier === SUBSCRIPTION_TIERS.PREMIUM;
  
  // Check if expired
  const showExpiredBanner = isExpired && currentTier !== SUBSCRIPTION_TIERS.FREE;

  // Show loading overlay
  const showLoadingOverlay = [
    PurchaseState.PURCHASING,
    PurchaseState.VERIFYING,
    PurchaseState.RESTORING,
  ].includes(purchaseState);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white relative">
      {/* Loading Overlay - Prevents frozen appearance */}
      <AnimatePresence>
        {showLoadingOverlay && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center"
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              className="bg-white rounded-2xl p-8 mx-4 max-w-sm w-full text-center shadow-2xl"
            >
              <Loader2 className="w-12 h-12 text-teal-500 animate-spin mx-auto mb-4" />
              <p className="text-lg font-medium text-slate-800">
                {STATE_MESSAGES[purchaseState] || 'Processing...'}
              </p>
              <p className="text-sm text-slate-500 mt-2">
                Please don&apos;t close this screen
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Offline Banner */}
      {purchaseState === PurchaseState.OFFLINE && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-3">
          <div className="flex items-center gap-2 max-w-lg mx-auto">
            <WifiOff className="w-5 h-5 text-amber-600" />
            <span className="text-sm text-amber-800">
              You&apos;re offline. Some features may be unavailable.
            </span>
          </div>
        </div>
      )}

      {/* Expired Banner */}
      {showExpiredBanner && (
        <div className="bg-orange-50 border-b border-orange-200 px-4 py-3">
          <div className="flex items-center gap-2 max-w-lg mx-auto">
            <AlertCircle className="w-5 h-5 text-orange-600" />
            <span className="text-sm text-orange-800">
              Your subscription has expired. Your data is safe and read-only until you renew.
            </span>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="sticky top-0 z-10 bg-white/80 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={handleBack}
              className="p-2 rounded-full hover:bg-slate-100 transition-colors"
              disabled={isPurchasing}
            >
              <ArrowLeft className="w-5 h-5 text-slate-600" />
            </button>
            <h1 className="text-xl font-semibold text-slate-800">Choose Your Plan</h1>
          </div>
          
          {/* Restore Purchases Button (always visible on native) */}
          {isNativePurchaseSupported() && (
            <button
              onClick={handleRestore}
              disabled={isPurchasing}
              className="text-sm text-teal-600 hover:text-teal-700 font-medium disabled:opacity-50"
            >
              Restore Purchases
            </button>
          )}
        </div>
      </div>

      {/* Hero */}
      <div className="max-w-4xl mx-auto px-4 py-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-50 rounded-full mb-4">
            <Sparkles className="w-4 h-4 text-teal-600" />
            <span className="text-sm font-medium text-teal-700">
              Organize your family&apos;s important information
            </span>
          </div>
          <h2 className="text-3xl font-bold text-slate-800 mb-3">
            Keep Everything in One Place
          </h2>
          <p className="text-slate-600 max-w-xl mx-auto">
            From medical records to family documents, Forget Me Not helps you stay prepared for any situation.
          </p>
        </motion.div>

        {/* Current tier badge */}
        {currentTier !== SUBSCRIPTION_TIERS.FREE && !isExpired && (
          <div className="mb-6">
            <span className={`px-3 py-1.5 rounded-full text-sm font-medium ${
              currentTier === SUBSCRIPTION_TIERS.PREMIUM
                ? 'bg-teal-100 text-teal-700'
                : 'bg-purple-100 text-purple-700'
            }`}>
              Current plan: {currentTier === SUBSCRIPTION_TIERS.PREMIUM ? 'Premium' : 'Family'}
            </span>
          </div>
        )}
      </div>

      {/* Plans Grid */}
      <div className="max-w-4xl mx-auto px-4 pb-12">
        <div className="grid md:grid-cols-3 gap-6">
          {/* Free Tier */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className={`relative rounded-2xl border-2 p-6 transition-all ${
              currentTier === SUBSCRIPTION_TIERS.FREE && !isExpired
                ? 'border-slate-300 bg-slate-50'
                : 'border-slate-200 bg-white hover:border-slate-300'
            }`}
          >
            {currentTier === SUBSCRIPTION_TIERS.FREE && !isExpired && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-slate-500 text-white text-xs rounded-full">
                Current Plan
              </div>
            )}
            <div className="text-center mb-6">
              <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Shield className="w-6 h-6 text-slate-500" />
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-1">Free</h3>
              <div className="text-3xl font-bold text-slate-800">$0</div>
              <div className="text-sm text-slate-500">Forever</div>
            </div>
            <ul className="space-y-3 mb-6">
              {FREE_FEATURES.map((feature, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-slate-600">
                  <feature.icon className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                  <span>{feature.text}</span>
                </li>
              ))}
            </ul>
            <Button
              variant="outline"
              className="w-full"
              disabled={currentTier === SUBSCRIPTION_TIERS.FREE && !isExpired}
            >
              {currentTier === SUBSCRIPTION_TIERS.FREE && !isExpired ? 'Current Plan' : 'Free Forever'}
            </Button>
          </motion.div>

          {/* Premium Tier */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className={`relative rounded-2xl border-2 p-6 transition-all ${
              selectedPlan === 'premium_monthly' || requiredTier === SUBSCRIPTION_TIERS.PREMIUM
                ? 'border-teal-500 bg-teal-50/50 shadow-lg shadow-teal-100'
                : currentTier === SUBSCRIPTION_TIERS.PREMIUM && !isExpired
                ? 'border-teal-300 bg-teal-50'
                : 'border-slate-200 bg-white hover:border-teal-300'
            }`}
          >
            {currentTier === SUBSCRIPTION_TIERS.PREMIUM && !isExpired && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-teal-500 text-white text-xs rounded-full">
                Current Plan
              </div>
            )}
            <div className="absolute -top-3 right-4 px-3 py-1 bg-gradient-to-r from-amber-400 to-orange-400 text-white text-xs rounded-full flex items-center gap-1">
              <Star className="w-3 h-3" />
              Popular
            </div>
            <div className="text-center mb-6">
              <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Crown className="w-6 h-6 text-teal-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-1">Premium</h3>
              <div className="text-3xl font-bold text-teal-600">
                {getPriceDisplay('premium_monthly', '$2.99')}
              </div>
              <div className="text-sm text-slate-500">per month</div>
            </div>
            <ul className="space-y-3 mb-6">
              {PREMIUM_FEATURES.slice(0, 7).map((feature, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-slate-600">
                  <feature.icon className="w-4 h-4 text-teal-500 mt-0.5 flex-shrink-0" />
                  <span>{feature.text}</span>
                </li>
              ))}
            </ul>
            <Button
              onClick={() => handleSelectPlan('premium_monthly')}
              className={`w-full ${
                selectedPlan === 'premium_monthly'
                  ? 'bg-teal-600 hover:bg-teal-700'
                  : 'bg-teal-500 hover:bg-teal-600'
              }`}
              disabled={
                isPurchasing ||
                (currentTier === SUBSCRIPTION_TIERS.PREMIUM && !isExpired) ||
                currentTier === SUBSCRIPTION_TIERS.FAMILY
              }
            >
              {currentTier === SUBSCRIPTION_TIERS.PREMIUM && !isExpired
                ? 'Current Plan'
                : currentTier === SUBSCRIPTION_TIERS.FAMILY
                ? 'Included'
                : selectedPlan === 'premium_monthly'
                ? 'Selected'
                : 'Choose Premium'}
            </Button>
          </motion.div>

          {/* Family Tier */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className={`relative rounded-2xl border-2 p-6 transition-all ${
              selectedPlan === 'family_monthly' || requiredTier === SUBSCRIPTION_TIERS.FAMILY
                ? 'border-purple-500 bg-purple-50/50 shadow-lg shadow-purple-100'
                : currentTier === SUBSCRIPTION_TIERS.FAMILY && !isExpired
                ? 'border-purple-300 bg-purple-50'
                : 'border-slate-200 bg-white hover:border-purple-300'
            }`}
          >
            {currentTier === SUBSCRIPTION_TIERS.FAMILY && !isExpired && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-purple-500 text-white text-xs rounded-full">
                Current Plan
              </div>
            )}
            {requiredTier === SUBSCRIPTION_TIERS.FAMILY && (
              <div className="absolute -top-3 right-4 px-3 py-1 bg-purple-500 text-white text-xs rounded-full">
                Required
              </div>
            )}
            <div className="text-center mb-6">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-1">Family</h3>
              <div className="text-3xl font-bold text-purple-600">
                {getPriceDisplay('family_monthly', '$5.99')}
              </div>
              <div className="text-sm text-slate-500">per month</div>
            </div>
            <ul className="space-y-3 mb-6">
              {FAMILY_FEATURES.map((feature, i) => (
                <li key={i} className={`flex items-start gap-3 text-sm ${
                  feature.highlight ? 'text-purple-700 font-medium' : 'text-slate-600'
                }`}>
                  <feature.icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                    feature.highlight ? 'text-purple-500' : 'text-purple-400'
                  }`} />
                  <span>{feature.text}</span>
                </li>
              ))}
            </ul>
            <Button
              onClick={() => handleSelectPlan('family_monthly')}
              className={`w-full ${
                selectedPlan === 'family_monthly'
                  ? 'bg-purple-600 hover:bg-purple-700'
                  : 'bg-purple-500 hover:bg-purple-600'
              }`}
              disabled={isPurchasing || (currentTier === SUBSCRIPTION_TIERS.FAMILY && !isExpired)}
            >
              {currentTier === SUBSCRIPTION_TIERS.FAMILY && !isExpired
                ? 'Current Plan'
                : canUpgrade
                ? 'Upgrade to Family'
                : selectedPlan === 'family_monthly'
                ? 'Selected'
                : 'Choose Family'}
            </Button>
          </motion.div>
        </div>

        {/* Subscribe Button */}
        {selectedPlan && (currentTier === SUBSCRIPTION_TIERS.FREE || isExpired || canUpgrade) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-8 text-center"
          >
            <Button
              onClick={handlePurchase}
              size="lg"
              disabled={isPurchasing || purchaseState === PurchaseState.OFFLINE}
              className={`px-8 ${
                selectedPlan === 'family_monthly'
                  ? 'bg-purple-600 hover:bg-purple-700'
                  : 'bg-teal-600 hover:bg-teal-700'
              }`}
            >
              {isPurchasing ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5 mr-2" />
                  {canUpgrade && selectedPlan === 'family_monthly'
                    ? 'Upgrade Now'
                    : 'Subscribe Now'}
                </>
              )}
            </Button>
            <p className="text-sm text-slate-500 mt-3">
              Cancel anytime. Your data is always safe.
            </p>
          </motion.div>
        )}

        {/* Manage Subscription (for active subscribers) */}
        {currentTier !== SUBSCRIPTION_TIERS.FREE && !isExpired && (
          <div className="mt-8 text-center">
            <button
              onClick={handleManageSubscription}
              className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-800"
            >
              <ExternalLink className="w-4 h-4" />
              Manage Subscription
            </button>
          </div>
        )}

        {/* Platform-specific info */}
        <div className="mt-8 text-center text-xs text-slate-400">
          {isIOS() && (
            <p>
              Payment will be charged to your Apple ID account. Subscription automatically renews unless turned off at least 24 hours before the current period ends.
            </p>
          )}
          {isAndroid() && (
            <p>
              Payment will be charged to your Google Play account. Subscription automatically renews unless cancelled.
            </p>
          )}
          {isWeb() && (
            <p>
              Payments processed securely via Stripe. Cancel anytime from your account settings.
            </p>
          )}
        </div>

        {/* FAQ / Info */}
        <div className="mt-8 text-center text-sm text-slate-500">
          <p>
            Questions? Visit our <a href="/help" className="text-teal-600 hover:underline">Help Center</a> or contact support.
          </p>
        </div>
      </div>
    </div>
  );
}

export default SubscriptionPlansPage;
